import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import PlanLayout from "./components/PlanLayout";
import Loader from "../../Component/Loader/Loader";
import { getRequest, postRequest } from "../../utils/api";

// --- Types ---
interface Plan {
    _id: string;
    ppm_plan_title: string;
    ppm_plan_is_black_card: boolean;
}

interface Row {
  label: string;
  type: 'text' | 'bool';
  vals: Record<string, any>; // Key: Plan _id
}

interface Group {
  name: string;
  rows: Row[];
}

interface MatrixData {
  pm_featured_plan_id: string | null;
  pm_groups: Group[];
}

const CapablitiesMatrix = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [data, setData] = useState<MatrixData>({ pm_featured_plan_id: null, pm_groups: [] });
    const [search, setSearch] = useState('');
    const [closed, setClosed] = useState<Set<number>>(new Set());
    const [dragGroup, setDragGroup] = useState<number | null>(null);
    const [dragRow, setDragRow] = useState<{ gi: number; ri: number } | null>(null);

    useEffect(() => {
        fetchMatrixData();
    }, []);

    // --- Dynamic Default Generator (Structure Only) ---
    const getDefaultGroups = (): Group[] => {
        return [
            {
                name: 'Messaging',
                rows: [
                    { label: 'Included conversations', type: 'text', vals: {} },
                    { label: 'Campaigns & broadcast', type: 'bool', vals: {} },
                    { label: 'Flow builder', type: 'bool', vals: {} },
                ]
            },
            {
                name: 'Team',
                rows: [
                    { label: 'Agent seats', type: 'text', vals: {} },
                    { label: 'SSO & SCIM', type: 'bool', vals: {} },
                ]
            },
            {
                name: 'Platform',
                rows: [
                    { label: 'API rate limit', type: 'text', vals: {} },
                    { label: 'Uptime SLA', type: 'text', vals: {} },
                    { label: 'Support response', type: 'text', vals: {} },
                ]
            },
        ];
    };

    const fetchMatrixData = async () => {
        setLoading(true);
        try {
            const response: any = await getRequest(`/plans/matrix`);
            if (response.status === "success") {
                const fetchedPlans = response.data.plans;
                const fetchedMatrix = response.data.matrix;
                setPlans(fetchedPlans);

                const isFirstTime = !fetchedMatrix || !fetchedMatrix.pm_groups || fetchedMatrix.pm_groups.length === 0;

                if (isFirstTime) {
                    setData({
                        pm_featured_plan_id: fetchedPlans.length > 0 ? fetchedPlans[fetchedPlans.length - 1]._id : null,
                        pm_groups: getDefaultGroups()
                    });
                } else {
                    setData(fetchedMatrix);
                }
            }
        } catch (error) {
            toast.error("Failed to load matrix data.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const response: any = await postRequest(`/plans/matrix/update`, data);
            if (response.status === "success") {
                toast.success('Matrix saved across all plans');
            } else {
                toast.error(response.message || "Failed to save matrix.");
            }
        } catch (error) {
            toast.error("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setData({
            pm_featured_plan_id: plans.length > 0 ? plans[plans.length - 1]._id : null,
            pm_groups: getDefaultGroups()
        });
        setClosed(new Set());
        toast.info('Reset to default structure');
    };

    // --- Handlers: Structural Ops ---
    const handleAddGroup = () => {
        setData(prev => ({
            ...prev,
            pm_groups: [
                ...prev.pm_groups,
                {
                    name: 'New group',
                    rows: [{ label: 'New capability', type: 'bool', vals: {} }]
                }
            ]
        }));
        toast.success('Group added');
    };

    const handleDelGroup = (gi: number) => {
        setData(prev => {
            const newGroups = [...prev.pm_groups];
            newGroups.splice(gi, 1);
            return { ...prev, pm_groups: newGroups };
        });
        setClosed(new Set());
    };

    const handleAddCap = (gi: number) => {
        setData(prev => {
            // FIX: Use deep copy to prevent React 18 strict mode from pushing twice
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            newGroups[gi].rows.push({
                label: 'New capability',
                type: 'bool',
                vals: {}
            });
            return { ...prev, pm_groups: newGroups };
        });
    };

    const handleDelRow = (gi: number, ri: number) => {
        setData(prev => {
            // FIX: Deep copy to prevent state mutation bugs
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            newGroups[gi].rows.splice(ri, 1);
            if (newGroups[gi].rows.length === 0) newGroups.splice(gi, 1);
            return { ...prev, pm_groups: newGroups };
        });
    };

    const handleSwitchType = (gi: number, ri: number) => {
        setData(prev => {
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            const r = newGroups[gi].rows[ri];
            r.type = r.type === 'bool' ? 'text' : 'bool';

            Object.keys(r.vals).forEach(planId => {
                if (r.type === 'text') r.vals[planId] = r.vals[planId] ? '✓' : '—';
                else r.vals[planId] = /^(✓|yes|true|✔|●)$/i.test(String(r.vals[planId]).trim());
            });

            return { ...prev, pm_groups: newGroups };
        });
    };

    const handleToggleBool = (gi: number, ri: number, planId: string) => {
        setData(prev => {
            // FIX: Deep copy to ensure safe toggling
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            const currentVal = newGroups[gi].rows[ri].vals[planId];
            newGroups[gi].rows[ri].vals[planId] = !currentVal;
            return { ...prev, pm_groups: newGroups };
        });
    };

    // --- Handlers: Inline Text Editing ---
    const handleUpdateGroupName = (gi: number, val: string) => {
        setData(prev => {
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            newGroups[gi].name = val || newGroups[gi].name;
            return { ...prev, pm_groups: newGroups };
        });
    };

    const handleUpdateRowLabel = (gi: number, ri: number, val: string) => {
        setData(prev => {
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            newGroups[gi].rows[ri].label = val || newGroups[gi].rows[ri].label;
            return { ...prev, pm_groups: newGroups };
        });
    };

    const handleUpdateCellText = (gi: number, ri: number, planId: string, val: string) => {
        setData(prev => {
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            newGroups[gi].rows[ri].vals[planId] = val || '—';
            return { ...prev, pm_groups: newGroups };
        });
    };

    // --- Handlers: Drag-to-reorder ---
    const moveGroup = (from: number, to: number) => {
        if (from === to) return;
        setData(prev => {
            const newGroups = [...prev.pm_groups];
            const [moved] = newGroups.splice(from, 1);
            newGroups.splice(to, 0, moved);
            return { ...prev, pm_groups: newGroups };
        });
    };

    // Rows only reorder within their own group — dragging across groups isn't supported.
    const moveRow = (gi: number, from: number, to: number) => {
        if (from === to) return;
        setData(prev => {
            const newGroups = JSON.parse(JSON.stringify(prev.pm_groups));
            const rows = newGroups[gi].rows;
            const [moved] = rows.splice(from, 1);
            rows.splice(to, 0, moved);
            return { ...prev, pm_groups: newGroups };
        });
    };

    const toggleGroup = (gi: number) => setClosed(prev => {
        const next = new Set(prev);
        if (next.has(gi)) next.delete(gi); else next.add(gi);
        return next;
    });

    const q = search.trim().toLowerCase();
    const totalRows = data.pm_groups.reduce((a, g) => a + g.rows.length, 0);
    const cols = plans.length + 2;

    return (
        <>
            {loading && <Loader />}
            <PlanLayout actions={<>
                <button type="button" className="cx-btn cx-btn-light" onClick={handleAddGroup}><i className="ri-folder-add-line"></i>Add group</button>
                <button type="button" className="cx-btn cx-btn-light" onClick={handleReset}><i className="ri-arrow-go-back-line"></i>Reset</button>
                <button type="button" className="cx-btn cx-btn-primary" onClick={handleSave}><i className="ri-check-line"></i>Save matrix</button>
            </>}>
                <div className="cx-card">
                    <div className="cx-toolbar">
                        <input className="cx-input" type="search" placeholder="Search capability…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        <div className="sp"></div>
                        <div className="cx-legend">
                            <span><i className="ri-check-line" style={{ color: 'var(--signal)', fontSize: 14 }}></i>Included</span>
                            <span><i className="ri-subtract-line" style={{ color: '#c0c4cc', fontSize: 14 }}></i>Not included</span>
                            <span><b className="mono" style={{ fontSize: 11 }}>abc</b>Value</span>
                        </div>
                    </div>
                    <div className="cx-tbl-wrap">
                        <table className="cx-tbl cx-cap-tbl">
                            <thead>
                                <tr>
                                    <th className="stk">Capability</th>
                                    {plans.map((plan) => <th key={plan._id}>{plan.ppm_plan_title}</th>)}
                                    <th className="r">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.pm_groups.map((g, gi) => {
                                    const rows = g.rows.map((row, ri) => ({ row, ri })).filter(({ row }) => !q || row.label.toLowerCase().includes(q));
                                    if (q && rows.length === 0) return null;
                                    const isClosed = closed.has(gi) && !q;
                                    return (
                                        <React.Fragment key={gi}>
                                            <tr
                                                className={`cx-cap-g ${isClosed ? 'closed' : ''}`}
                                                style={dragGroup === gi ? { opacity: 0.4 } : undefined}
                                                onDragOver={(e) => { if (dragGroup !== null) e.preventDefault(); }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    if (dragGroup !== null) { moveGroup(dragGroup, gi); setDragGroup(null); }
                                                }}
                                            >
                                                <td className="stk" colSpan={cols - 1}>
                                                    <span
                                                        className="tg"
                                                        title="Drag to reorder group"
                                                        draggable
                                                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(gi)); setDragGroup(gi); }}
                                                        onDragEnd={() => setDragGroup(null)}
                                                        style={{ cursor: 'grab' }}
                                                    >
                                                        <i className="ri-draggable"></i>
                                                    </span>
                                                    <span className="tg" title={isClosed ? 'Expand' : 'Collapse'} onClick={() => toggleGroup(gi)}><i className="ri-arrow-down-s-line"></i></span>
                                                    <span
                                                        className="nm"
                                                        contentEditable
                                                        suppressContentEditableWarning
                                                        spellCheck={false}
                                                        onBlur={(e) => handleUpdateGroupName(gi, e.currentTarget.textContent || '')}
                                                    >
                                                        {g.name}
                                                    </span>
                                                    <span className="muted fs12"> · {g.rows.length}</span>
                                                </td>
                                                <td>
                                                    <div className="cx-ra">
                                                        <button type="button" title="Delete group" onClick={() => handleDelGroup(gi)}><i className="ri-delete-bin-line"></i></button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {!isClosed && rows.map(({ row, ri }) => (
                                                <tr
                                                    key={ri}
                                                    style={dragRow && dragRow.gi === gi && dragRow.ri === ri ? { opacity: 0.4 } : undefined}
                                                    onDragOver={(e) => { if (dragRow && dragRow.gi === gi) e.preventDefault(); }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        if (dragRow && dragRow.gi === gi) { moveRow(gi, dragRow.ri, ri); setDragRow(null); }
                                                    }}
                                                >
                                                    <td className="stk">
                                                        <span
                                                            title="Drag to reorder capability"
                                                            draggable
                                                            onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(ri)); setDragRow({ gi, ri }); }}
                                                            onDragEnd={() => setDragRow(null)}
                                                            style={{ cursor: 'grab', marginRight: 6, display: 'inline-block' }}
                                                        >
                                                            <i className="ri-draggable"></i>
                                                        </span>
                                                        <span
                                                            contentEditable
                                                            suppressContentEditableWarning
                                                            spellCheck={false}
                                                            onBlur={(e) => handleUpdateRowLabel(gi, ri, e.currentTarget.textContent || '')}
                                                        >
                                                            {row.label}
                                                        </span>
                                                    </td>
                                                    {plans.map((plan) => {
                                                        const val = row.vals[plan._id];
                                                        return (
                                                            <td key={plan._id}>
                                                                {row.type === 'bool' ? (
                                                                    <button type="button" className={`cx-cap-b ${val ? 'on' : ''}`} onClick={() => handleToggleBool(gi, ri, plan._id)}>
                                                                        <i className={val ? 'ri-check-line' : 'ri-subtract-line'}></i>
                                                                    </button>
                                                                ) : (
                                                                    <span
                                                                        className="cx-cap-v"
                                                                        contentEditable
                                                                        suppressContentEditableWarning
                                                                        spellCheck={false}
                                                                        onBlur={(e) => handleUpdateCellText(gi, ri, plan._id, e.currentTarget.textContent || '')}
                                                                    >
                                                                        {val || '—'}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                    <td>
                                                        <div className="cx-ra">
                                                            <button type="button" title={row.type === 'bool' ? 'Switch to text value' : 'Switch to ✓ / –'} onClick={() => handleSwitchType(gi, ri)}>
                                                                <i className={row.type === 'bool' ? 'ri-text' : 'ri-toggle-line'}></i>
                                                            </button>
                                                            <button type="button" title="Delete row" onClick={() => handleDelRow(gi, ri)}><i className="ri-delete-bin-line"></i></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}

                                            {!isClosed && !q && (
                                                <tr className="addrow">
                                                    <td className="stk" colSpan={cols}>
                                                        <button type="button" className="cx-cap-add" onClick={() => handleAddCap(gi)}>
                                                            <i className="ri-add-line"></i>Add capability to {g.name}
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {data.pm_groups.length === 0 && (
                                    <tr><td colSpan={cols}><div className="cx-empty"><i className="ri-layout-grid-line"></i>No capabilities yet. Use “Add group” to start.</div></td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="cx-tfoot">
                        <span>Click a <b>✓ / –</b> to toggle · click any text to edit it · drag <i className="ri-draggable"></i> to reorder groups or capabilities. Columns sync automatically with live plans.</span>
                        <span className="mono">{totalRows} capabilities × {plans.length} plans</span>
                    </div>
                </div>
            </PlanLayout>
        </>
    )
}
export default CapablitiesMatrix;
